
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import EditBuilding from "@/components/buildings/EditBuilding.vue";
import OpenDoorDialog from "@/components/non-transferables/OpenDoorDialog.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiAccountKey, mdiDoorOpen, mdiFormatListBulletedType } from "@mdi/js";
import EditNonTransferable from "@/components/non-transferables/EditNonTransferable.vue";
import EditBtn from "@/components/generic/buttons/edit-btn.vue";
import router from "@/router";
import { haversineDistance } from "@/utils/misc";
import GeolocationHelpDialog from "@/components/GeolocationHelpDialog.vue";

@Component({
  components: {
    GeolocationHelpDialog,
    EditBtn,
    EditNonTransferable,
    OpenDoorDialog,
    EditBuilding,
    SvgIcon,
  },
})
export default class NonTransferablePage extends Vue {

  get hasKeyAccess(): boolean {
    return !!this.nonTransferable?.legitimateKeyAccess;
  }

  get hasManageRight(): boolean {
    return this.canDoInEnvCompany("MANAGE_NON_TRANSFERABLES");
  }

  get canOpenDoorWithoutGeoloc(): boolean {
    return this.canDoInEnvCompany("OPEN_DOOR_WITHOUT_GEOLOC");
  }

  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private isMobile: boolean;

  private nonTransferableId: number | null = null;
  private nonTransferable: any = null;
  private loadingGetNonTransferable: boolean = false;
  private tab: "GUESTS"|"LOGS" = "GUESTS";
  private loadingTab: boolean = false;
  private editNonTransferableDialog: boolean = false;
  private openDoorDialog: boolean = false;
  // geoloc
  private geolocNotSupported: boolean = false;
  private geolocError: boolean = false;
  private displayHelpGeolocation: boolean = false;
  private userLatitude: number | null = null;
  private userLongitude: number | null = null;
  private distance: number | null = null;
  private MAX_DISTANCE = 100; // meters

  private icons = {
    mdiDoorOpen,
    mdiAccountKey,
    mdiFormatListBulletedType,
  };

  public getUserPosition() {
    this.geolocError = false;
    this.geolocNotSupported = false;
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.userLatitude = position.coords.latitude;
            this.userLongitude = position.coords.longitude;
            resolve({ userLatitude: this.userLatitude, userLongitude: this.userLongitude });
          },
          (error) => {
            // tslint:disable-next-line:no-console
            console.error("Geolocation error:", error);
            this.geolocError = true;
          },
        );
      } else {
        // tslint:disable-next-line:no-console
        console.warn("Geolocation not supported.");
        this.geolocNotSupported = true;
      }
    });
  }

  private async mounted(): Promise<void> {
    this.nonTransferableId = Number(this.$route.params?.id);
    // Only for user accessing root page, not specific tabs
    if (this.$route.name === "non-transferable" && this.hasManageRight) {
      await this.$router.replace({ name: "non-transferable-guests" });
    }
    await this.reset();
    // Check immediately
    await this.computeGeolocationData();
    // Add watcher to compute automatically if permission change
    navigator.permissions.query({ name: "geolocation" as PermissionName })
      .then((result) => {
        result.onchange = () => {
          this.computeGeolocationData();
        };
      });
  }

  private async computeGeolocationData(): Promise<void> {
    this.distance = null;
    this.userLatitude = null;
    this.userLongitude = null;
    if (!this.canOpenDoorWithoutGeoloc
      && this.nonTransferable?.Building?.Address?.latitude
      && this.nonTransferable?.Building?.Address?.longitude
    ) {
      await this.getUserPosition().then(() => {
        if (this.userLatitude && this.userLongitude) {
          this.distance = haversineDistance(
            this.nonTransferable!.Building!.Address!.latitude,
            this.nonTransferable!.Building!.Address!.longitude,
            this.userLatitude,
            this.userLongitude,
          );
        }
      });
    }
  }

  private async reset(): Promise<void> {
    this.editNonTransferableDialog = false;
    await this.getNonTransferableById();
  }

  private async deleted(): Promise<void> {
    await this.$router.push({
      name: "building-non-transferables",
      params: { id: this.nonTransferable.buildingId },
    });
  }

  private async successOpenDoorFn(): Promise<void> {
    // If user has no more access to the non-transferable, redirect to invitations page to prevent error 403 display
    const isPunctualAccess = this.nonTransferable?.legitimateKeyAccess?.countAccessLeft === 1;
    if (isPunctualAccess && !this.hasManageRight) {
      await router.push({name: "key-access"});
    } else {
      await this.getNonTransferableById();
    }
  }

  private async getNonTransferableById(): Promise<void> {
    this.loadingGetNonTransferable = true;
    await this.$store.dispatch("nonTransferables/readById", {
      id: this.nonTransferableId,
    }).then((data) => {
      this.nonTransferable = data;
    }).catch(async (error) => {
      this.$store.commit("alerts/displayHttpError", { error });
      // If user has no access to non-transferable (via keyAccess or right), redirect
      if (!this.hasKeyAccess && !this.hasManageRight) {
        await router.push({name: "key-access"});
      }
    }).finally(() => {
      this.loadingGetNonTransferable = false;
    });
  }
}
